import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FlightIcon from '@mui/icons-material/Flight';
import ComputerIcon from '@mui/icons-material/Computer';
import {
	AuthorizationChannel,
	FISERV_PORTAL_URL,
	getChannelLabel,
	isAmexStamp,
	resolveCallCenterPhone,
} from 'features/authorization/constants/authorizationChannels';
import { copyToClipboard } from 'features/transaction-details/helpers';

interface AuthorizationChannelGuideProps {
	channel: AuthorizationChannel;
	carrierName: string;
	cardStamp: string;
	cardStampLabel: string;
	onCopied?: (message: string) => void;
}

export const AuthorizationChannelGuide = ({
	channel,
	carrierName,
	cardStamp,
	cardStampLabel,
	onCopied,
}: AuthorizationChannelGuideProps): JSX.Element => {
	const callCenterPhone = resolveCallCenterPhone(cardStamp);
	const isAmex = isAmexStamp(cardStamp);

	const handleCopyPhone = async (): Promise<void> => {
		const success = await copyToClipboard(callCenterPhone);
		if (success) onCopied?.('Teléfono copiado');
	};

	if (channel === 'fiserv') {
		return (
			<Alert severity="info" icon={<ComputerIcon />} sx={{ border: 1, borderColor: 'info.light' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Autorizá en Fiserv
				</Typography>
				<Typography variant="body2" gutterBottom>
					{carrierName} con sello {cardStampLabel}: usá el panel Fiserv.
				</Typography>
				<Button
					variant="contained"
					size="small"
					startIcon={<OpenInNewIcon />}
					href={FISERV_PORTAL_URL}
					target="_blank"
					rel="noopener noreferrer"
					tabIndex={-1}
				>
					Abrir Fiserv
				</Button>
			</Alert>
		);
	}

	if (channel === 'call_center') {
		return (
			<Alert severity="warning" icon={<PhoneIcon />} sx={{ border: 1, borderColor: 'warning.light' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Autorizá por call center
				</Typography>
				<Typography variant="body2" gutterBottom>
					{carrierName} con <strong>{cardStampLabel}</strong>: no uses Fiserv. Llamá al call center{' '}
					{isAmex ? 'de American Express (AX)' : 'de Visa'} e ingresá los datos manualmente.
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
					<Typography variant="h5" fontWeight="bold" fontFamily="monospace">
						{callCenterPhone}
					</Typography>
					<Button variant="outlined" size="small" onClick={handleCopyPhone} tabIndex={-1}>
						Copiar teléfono
					</Button>
				</Box>
			</Alert>
		);
	}

	if (channel === 'gds') {
		return (
			<Alert severity="secondary" icon={<FlightIcon />} sx={{ border: 1, borderColor: 'divider' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Autorización por GDS
				</Typography>
				<Typography variant="body2">
					{carrierName}: venta internacional, un solo pago. La autorización se gestiona por GDS, no por Fiserv ni call
					center.
				</Typography>
			</Alert>
		);
	}

	if (channel === 'no_cards') {
		return (
			<Alert severity="error">
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Tarjeta no habilitada
				</Typography>
				<Typography variant="body2">
					{cardStampLabel} no está habilitada para autorización. Verificá el procedimiento con supervisión antes de
					continuar.
				</Typography>
			</Alert>
		);
	}

	return (
		<Alert severity="warning">
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				{getChannelLabel(channel)}
			</Typography>
			<Typography variant="body2">
				No hay regla automática para {carrierName}. Consultá el procedimiento vigente.
			</Typography>
		</Alert>
	);
};
